import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, BuildingOfficeIcon, PencilIcon, TrashIcon, ArchiveBoxIcon, ArchiveBoxXMarkIcon, CogIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useMsal } from '@azure/msal-react';
import apiClient, { getUserCompanyIds, isUserAdmin } from '../../services/apiClient';
import { Customer } from '../../types';
import RequireRole from '../auth/RequireRole';
import CustomerSharePointCredentialsForm from './CustomerCredentialsForm';

const CustomerSelector: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { instance } = useMsal();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', description: '' });
  const [isActiveOpen, setIsActiveOpen] = useState(true);
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [showCredentialsForm, setShowCredentialsForm] = useState<string | null>(null);

  // Get user's company IDs and admin status
  const userCompanyIds = useMemo(() => getUserCompanyIds(), []);
  const isAdmin = useMemo(() => isUserAdmin(), []);

  const { data: allCustomers, isLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/customers');
        console.log('Customers:', response.data);
        return response.data || [];
      } catch (error) {
        return [];
      }
    }
  });

  // Filter customers based on user roles
  const customers = useMemo(() => {
    if (!allCustomers) return [];
    
    // Admin users see all companies
    if (isAdmin) {
      return allCustomers;
    }
    
    // Non-admin users only see companies they have access to
    return allCustomers.filter((customer: Customer) => {
      // Check if user has access to this company
      return userCompanyIds.includes(customer.id);
    });
  }, [allCustomers, userCompanyIds, isAdmin]);

  const createCustomerMutation = useMutation({
    mutationFn: async (customer: { name: string; description: string }) => {
      const response = await apiClient.post('/customers', customer);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Customer created successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setShowCreateForm(false);
      setNewCustomer({ name: '', description: '' });
    },
    onError: () => {
      toast.error('Failed to create customer');
    }
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: async (customerId: string) => {
      const response = await apiClient.deleteCustomer(customerId);
      return response;
    },
    onSuccess: () => {
      toast.success('Customer deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: () => {
      toast.error('Failed to delete customer');
    }
  });

  const archiveCustomerMutation = useMutation({
    mutationFn: async (customerId: string) => {
      const response = await apiClient.archiveCustomer(customerId);
      return response;
    },
    onSuccess: () => {
      toast.success('Customer archived successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: () => {
      toast.error('Failed to archive customer');
    }
  });

  const unarchiveCustomerMutation = useMutation({
    mutationFn: async (customerId: string) => {
      const response = await apiClient.unarchiveCustomer(customerId);
      return response;
    },
    onSuccess: () => {
      toast.success('Customer unarchived successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: () => {
      toast.error('Failed to unarchive customer');
    }
  });

  const renameCustomerMutation = useMutation({
    mutationFn: async ({ customerId, newName }: { customerId: string; newName: string }) => {
      const response = await apiClient.renameCustomer(customerId, newName);
      return response;
    },
    onSuccess: () => {
      toast.success('Customer renamed successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setEditingCustomer(null);
      setEditingName('');
    },
    onError: () => {
      toast.error('Failed to rename customer');
    }
  });

  const handleSelectCustomer = (customer: Customer) => {
    navigate(`/customers/${customer.id}/phases`);
  };

  const handleCreateCustomer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustomer.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    createCustomerMutation.mutate(newCustomer);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({ ...prev, [name]: value }));
  };

  const handleDeleteCustomer = (customerId: string, customerName: string) => {
    if (window.confirm(`Are you sure you want to delete "${customerName}"? This action cannot be undone.`)) {
      deleteCustomerMutation.mutate(customerId);
    }
  };

  const handleArchiveCustomer = (customerId: string) => {
    archiveCustomerMutation.mutate(customerId);
  };

  const handleUnarchiveCustomer = (customerId: string) => {
    unarchiveCustomerMutation.mutate(customerId);
  };

  const handleStartRename = (customerId: string, currentName: string) => {
    setEditingCustomer(customerId);
    setEditingName(currentName);
  };

  const handleCancelRename = () => {
    setEditingCustomer(null);
    setEditingName('');
  };

  const handleSaveRename = (customerId: string) => {
    if (!editingName.trim()) {
      toast.error('Customer name cannot be empty');
      return;
    }
    renameCustomerMutation.mutate({ customerId, newName: editingName.trim() });
  };

  const handleShowCredentials = (customerId: string) => {
    setShowCredentialsForm(customerId);
  };

  const handleCloseCredentials = () => {
    setShowCredentialsForm(null);
  };


  const active = useMemo(() => (customers || []).filter((c: any) => !c.archived), [customers]);
  const archived = useMemo(() => (customers || []).filter((c: any) => c.archived), [customers]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Customer Management</h1>
            <p className="text-gray-600 mt-2">Select a customer to manage their migration phases</p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            New Customer
          </button>
        </div>
        
        {/* Role-based access info */}
        {!isAdmin && userCompanyIds.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Access Level:</strong> You have access to {userCompanyIds.length} {userCompanyIds.length !== 1 ? 'companies' : 'company'} based on your role. 
              Showing {customers.length} of {allCustomers?.length || 0} total companies.
            </p>
          </div>
        )}
      </div>

      {showCreateForm && (
        <div className="card mb-8">
          <h2 className="text-xl font-semibold mb-4">Create New Customer</h2>
          <form onSubmit={handleCreateCustomer} className="space-y-4">
            <div>
              <label className="form-label">Customer Name *</label>
              <input
                type="text"
                name="name"
                value={newCustomer.name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter customer name"
                required
              />
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={newCustomer.description}
                onChange={handleInputChange}
                className="form-input"
                rows={3}
                placeholder="Enter customer description"
              />
            </div>
            <div className="flex space-x-3">
              <button type="submit" className="btn-primary" disabled={createCustomerMutation.isPending}>
                {createCustomerMutation.isPending ? 'Creating...' : 'Create Customer'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Accordions */}
      <div className="space-y-4">
        {/* Active customers */}
        <div className="border rounded">
          <button
            type="button"
            onClick={() => setIsActiveOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-t"
          >
            <span className="text-sm font-semibold text-gray-800">Active customers</span>
            <span className="text-xs text-gray-500">{active.length}</span>
          </button>
          {isActiveOpen && (
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {active?.map((customer: Customer) => (
                <div
                  key={customer.id}
                  className="card hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-200"
                >
                  <div className="flex items-center mb-4">
                    <BuildingOfficeIcon className="w-8 h-8 text-blue-600 mr-3" />
                    <div className="flex-1">
                      {editingCustomer === customer.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="form-input text-sm flex-1"
                            onKeyPress={(e) => e.key === 'Enter' && handleSaveRename(customer.id)}
                          />
                          <button
                            onClick={() => handleSaveRename(customer.id)}
                            className="text-green-600 hover:text-green-800"
                            disabled={renameCustomerMutation.isPending}
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelRename}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{customer.name}</h3>
                          <p className="text-sm text-gray-500">ID: {customer.id.substring(0, 8)}...</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {customer.description && (
                    <p className="text-gray-600 mb-4">{customer.description}</p>
                  )}

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Created: {new Date(customer.createdAt).toLocaleDateString()}</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">Active</span>
                  </div>

                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <button onClick={() => handleSelectCustomer(customer)} className="btn-secondary text-sm">
                        Open
                      </button>
                      {!customer?.sharepoint?.connected ? (
                        <button
                          onClick={async () => {
                            try {
                              await apiClient.connectSharePoint(customer.id, {});
                              toast.success('SharePoint connected');
                              queryClient.invalidateQueries({ queryKey: ['customers'] });
                            } catch {
                              toast.error('Failed to connect SharePoint');
                            }
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Connect SharePoint
                        </button>
                      ) : (
                        <span className="text-xs text-green-700">SharePoint connected</span>
                      )}
                    </div>
                    
                    {isAdmin && (
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleShowCredentials(customer.id)}
                            className="text-blue-500 hover:text-blue-700 p-1"
                            title="Manage SharePoint credentials"
                          >
                            <CogIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleStartRename(customer.id, customer.name)}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            title="Rename customer"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleArchiveCustomer(customer.id)}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            title="Archive customer"
                            disabled={archiveCustomerMutation.isPending}
                          >
                            <ArchiveBoxIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCustomer(customer.id, customer.name)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Delete customer"
                            disabled={deleteCustomerMutation.isPending}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-xs text-gray-400">Admin</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {active?.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <BuildingOfficeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
                  <p className="text-gray-600 mb-6">Get started by creating your first customer</p>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="btn-primary"
                  >
                    Create Customer
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Archived customers */}
        <div className="border rounded">
          <button
            type="button"
            onClick={() => setIsArchivedOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-t"
          >
            <span className="text-sm font-semibold text-gray-800">Archived customers</span>
            <span className="text-xs text-gray-500">{archived.length}</span>
          </button>
          {isArchivedOpen && (
            <div className="p-4 space-y-2">
              {archived.map((c: any) => (
                <div key={c.id} className="p-3 border rounded flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{c.name}</div>
                    <div className="text-xs text-gray-500">Archived {c.archivedAt ? new Date(c.archivedAt).toLocaleDateString() : ''}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleSelectCustomer(c)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Open
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => handleUnarchiveCustomer(c.id)}
                          className="text-xs text-green-600 hover:text-green-800"
                          title="Unarchive customer"
                          disabled={unarchiveCustomerMutation.isPending}
                        >
                          <ArchiveBoxXMarkIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCustomer(c.id, c.name)}
                          className="text-xs text-red-500 hover:text-red-800"
                          title="Delete customer"
                          disabled={deleteCustomerMutation.isPending}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {archived.length === 0 && (
                <div className="text-xs text-gray-500 p-2">No archived customers</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* SharePoint Credentials Form Modal */}
      {showCredentialsForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <CustomerSharePointCredentialsForm 
              customerId={showCredentialsForm} 
              onClose={handleCloseCredentials}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSelector;